// SPEC-METER-EXCHANGE-REPORT-01 (D-994): メーター交換/一周の「区切り」を 1 つの式で計算する共通モジュール。
//
// ★背景(D-984 phase1 の続き)
//   交換/一周はメモ(set_o)の文字を parseMeterNote が読んで is_meter_reset / prev_meter_final /
//   meter_carry_in / meter_carry_out に変換していた。現場で 3 種類の事故が出た(2026-10-05):
//     1. 同じ日に 8 回保存すると 2 回目以降は「今日の値」と比べて減っていないので一周の繰り上がりが消えた
//        (FKS01-M40-B04、prev_meter_final が NULL のまま)。
//     2. 月次実績(fn_monthly_machine_actuals)だけが一周の繰り上がりを足していなかった(FKS01-M40 10 月 6,900 円、
//        正しくは 9,700 円)。集金の累計(fn_booth_collection_accum_ext)は足していた = 式が 2 つあった。
//     3. OUT(景品)メーターだけ交換したのに、行単位のフラグなので IN まで区切られ、IN の値が丸ごと
//        9 月の売上になった(BYS01-M04-B04 / M05-B04、約 ¥3.1M)。
//
// ★D-994 の決定
//   - 交換/一周は「メーター交換・一周」ボタンのシートで報告する構造化データ(meter_readings.meter_exchanges、
//     jsonb 配列)だけで表す。メモの文字は計算に使わない(AC2)。
//   - 1 件 = 1 本のメーター。IN だけ・OUT だけを別々に表せる(AC4)。
//   - 差分の式はこのファイルの meterBoundaryDelta と SQL の public.fn_meter_boundary_delta の 2 つだけで、
//     両者は同じ表(src/__tests__/fixtures/meterBoundaryCases.js)で突き合わせる。
//
// 1 件の形(DB の CHECK 制約 fn_meter_exchanges_valid と同じ):
//   { meter: 'in_meter'|'in_meter_2'|'in_meter_3'|'in_meter_4'|'out_meter'|'out_meter_2'|'out_meter_3'|'out_meter_4',
//     type:  'reset'(交換) | 'rollover'(一周),
//     final: 交換/一周の直前の値(旧メーターの最終値)。number|null,
//     new:   交換: 新しいメーターの取付時の値(既定 0。gate_3 差し戻し 1 回目 item5 の司令塔判断)/
//            一周: 区切り直後の値(参考。式には使わない)。number|null,
//     max:   一周の上限(999999 / 9999999 …)。交換は null }
//
// 式(SQL と 1 文字ずつ対応させている):
//   イベント無し     : cur − prev
//   一周 (rollover)  : (final − prev) + (max + 1 − final) + cur
//                      final 省略時は prev。max 省略時は final の桁数から推定(999…9)。
//                      cur ≥ final(一周していない)/ final < prev / max < final は null(計算しない)。
//   交換 (reset)     : (final − prev) + (cur − new)
//                      new = 新しいメーターの取付時の値。省略時は 0(旧月次式 (final − lag) + in_meter と一致)。
//                      (cur − new) = その日に新メーターで回った分。
//                      final が無い / final < prev は null(推測で埋めない、RFC 2863 3.1.5 の考え方)。
//                      new < final(下がったと報告)なのに cur ≥ final(メーターが下がっていない)も null(D-1073)。
//                      new ≥ final(中古メーター型: 新しいメーターが旧最終値より大きい値から始まる。本番 HMN01-M04 5/12)は計算する。
//   null は「分からない」。0 にも満額にもしない(AC3)。呼び出し側は null を 0 扱い + 要確認にする。

export const METER_KEYS = Object.freeze([
  'in_meter', 'in_meter_2', 'in_meter_3', 'in_meter_4',
  'out_meter', 'out_meter_2', 'out_meter_3', 'out_meter_4',
])
export const IN_METER_KEYS = Object.freeze(METER_KEYS.filter(k => k.startsWith('in_')))
export const OUT_METER_KEYS = Object.freeze(METER_KEYS.filter(k => k.startsWith('out_')))
export const EXCHANGE_TYPES = Object.freeze(['reset', 'rollover'])

/** 画面表示用のメーター名。★DB のキーは変えない。 */
export const METER_LABELS = Object.freeze({
  in_meter: 'IN', in_meter_2: 'IN2', in_meter_3: 'IN3', in_meter_4: 'IN4',
  out_meter: 'OUT', out_meter_2: 'OUT2', out_meter_3: 'OUT3', out_meter_4: 'OUT4',
})
export const EXCHANGE_TYPE_LABELS = Object.freeze({ reset: '交換', rollover: '一周' })

/** 数値か null。空文字・非数は null(0 にしない)。 */
export function numOrNull(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * 一周の上限を「旧メーターの最終値の桁数」から推定する(AC3)。
 * ★機種マスタを見ない(D-550: 世代で 6 桁/7 桁が混ざる)。9999991 → 9999999、999892 → 999999。
 * SQL: public.fn_meter_rollover_max
 */
export function inferRolloverMax(final) {
  const n = numOrNull(final)
  if (n == null || n < 0) return null
  const digits = String(Math.trunc(n)).length
  return 10 ** digits - 1
}

/** 一周の繰り上がり = max + 1 − final(AC3)。どちらか欠けたら null。 */
export function rolloverCarry(final, max) {
  const f = numOrNull(final)
  const m = numOrNull(max ?? inferRolloverMax(final))
  if (f == null || m == null || m < f) return null
  return m + 1 - f
}

/**
 * D-1073: 交換で「下がったと報告しているのに、メーターが下がっていない」か。
 *   new < final(新しいメーターは旧最終値より小さい値から始まると報告)かつ cur >= final。
 *   new >= final(中古メーター型)は対象外(今までどおり計算する)。SQL fn_meter_boundary_delta と同じ条件。
 */
function resetNotDropped(f, nv, c) {
  return nv < f && c >= f
}

/**
 * 区切りをまたぐ差分。★唯一の式(SQL public.fn_meter_boundary_delta と同じ)。
 * @param {number|string|null} prev 前回の値(前の日のこのメーター)
 * @param {number|string|null} cur  今回の値
 * @param {object|null} ev このメーターのイベント(pickExchange の戻り値)。null = イベント無し
 * @returns {number|null}
 */
export function meterBoundaryDelta(prev, cur, ev) {
  const p = numOrNull(prev)
  const c = numOrNull(cur)
  if (p == null || c == null) return null
  if (ev == null || typeof ev !== 'object') return c - p
  if (ev.type === 'rollover') {
    const f = numOrNull(ev.final) ?? p
    const m = numOrNull(ev.max) ?? inferRolloverMax(f)
    if (m == null || f < p || m < f || c >= f) return null
    return (f - p) + (m + 1 - f) + c
  }
  if (ev.type === 'reset') {
    const f = numOrNull(ev.final)
    if (f == null || f < p) return null
    const nv = numOrNull(ev.new) ?? 0
    // D-1073: 下がったと報告(new < final)しているのにメーターが下がっていない(cur >= final)は判定できない = null。
    if (resetNotDropped(f, nv, c)) return null
    return (f - p) + (c - nv)
  }
  return null
}

/**
 * 2 点引き算(集金: 今回IN − 前回IN)に足し戻す量。イベント 1 件ぶん。
 * = meterBoundaryDelta(p, c, ev) − (c − p) を p, c に依存しない形に整理したもの。
 *   一周: (f − p) + (m + 1 − f) + c − (c − p) = m + 1
 *   交換: (f − p) + (c − new) − (c − p)       = f − new
 * ★final/new が分からない交換は null(補正不能 = 画面で要確認)。
 * D-1073: cur(その日のイベント行の今回値)が分かり、下がったと報告して下がっていない交換(meterBoundaryDelta が null)も null。
 *   cur が分からないとき(省略)は今までどおり final − new。
 * @param {object|null} ev
 * @param {number|string|null} [cur] その日のイベント行の今回値
 */
export function exchangeCompensation(ev, cur) {
  if (ev == null || typeof ev !== 'object') return 0
  if (ev.type === 'rollover') {
    const m = numOrNull(ev.max) ?? inferRolloverMax(ev.final)
    return m == null ? null : m + 1
  }
  if (ev.type === 'reset') {
    const f = numOrNull(ev.final)
    if (f == null) return null
    const nv = numOrNull(ev.new) ?? 0
    const c = numOrNull(cur)
    if (c != null && resetNotDropped(f, nv, c)) return null
    return f - nv
  }
  return null
}

/** 1 件を正規化する。形が壊れていれば null。 */
export function normalizeExchange(ev) {
  if (ev == null || typeof ev !== 'object' || Array.isArray(ev)) return null
  if (!METER_KEYS.includes(ev.meter)) return null
  if (!EXCHANGE_TYPES.includes(ev.type)) return null
  const out = { meter: ev.meter, type: ev.type, final: numOrNull(ev.final), new: numOrNull(ev.new), max: null }
  if (ev.type === 'rollover') out.max = numOrNull(ev.max) ?? inferRolloverMax(out.final)
  return out
}

/** 配列を正規化する(壊れた要素は捨てる、同じメーターは後勝ち)。null/非配列は null。 */
export function normalizeExchanges(list) {
  if (!Array.isArray(list)) return null
  const byMeter = new Map()
  for (const e of list) {
    const n = normalizeExchange(e)
    if (n) byMeter.set(n.meter, n)
  }
  return METER_KEYS.filter(k => byMeter.has(k)).map(k => byMeter.get(k))
}

/** 指定メーターのイベントを取り出す(SQL public.fn_meter_exchange_pick)。 */
export function pickExchange(list, meterKey) {
  if (!Array.isArray(list)) return null
  return list.find(e => e && e.meter === meterKey) ?? null
}

/** 2 つのイベント配列が同じ内容か(保存の「変更なし」判定用)。 */
export function sameExchanges(a, b) {
  const na = normalizeExchanges(a)
  const nb = normalizeExchanges(b)
  if (na == null || nb == null) return na == null && nb == null
  return JSON.stringify(na) === JSON.stringify(nb)
}

// ★時刻は数値に直して比べる(経路で '+00:00' と 'Z' が混ざるため文字列比較しない)。
const ts = (v) => { const n = Date.parse(v ?? ''); return Number.isFinite(n) ? n : -Infinity }
// SQL: greatest(created_at, coalesce(updated_at, created_at))
// SQL: greatest(coalesce(created_at, read_time), coalesce(updated_at, created_at, read_time))
const createdAt = (r) => ts(r?.created_at ?? r?.read_time)
const effAt = (r) => Math.max(createdAt(r), ts(r?.updated_at ?? r?.created_at ?? r?.read_time))

/**
 * その日の「イベント行」を決める(SQL public.fn_meter_exchange_rows と同じ規則)。
 *   同じ booth・同じ patrol_date の行のうち、meter_exchanges が null でない行の中で
 *   実効時刻(updated_at → created_at)が最も新しい 1 行。その配列が空なら「イベント無し」。
 *   ★meter_exchanges が null の行(シートを知らない保存・別スタッフの入替)は数えない
 *     = 報告を知らない行が後から保存されても報告は消えない(field failure 1 の対策)。
 * @param {Array<object>} rows 1 ブースの行(複数日混在可)
 * @returns {Map<string, object>} patrol_date → イベント行(配列が空でない日だけ)
 */
function latestDeclaringRows(rows) {
  const best = new Map()
  const isPatrol = (x) => (x?.entry_type ?? 'patrol') === 'patrol'
  for (const r of rows ?? []) {
    if (!r || r.meter_exchanges == null || !r.patrol_date) continue
    const d = String(r.patrol_date)
    const cur = best.get(d)
    if (!cur) { best.set(d, r); continue }
    const a = effAt(r), b = effAt(cur)
    if (a > b
      || (a === b && isPatrol(r) && !isPatrol(cur))
      || (a === b && isPatrol(r) === isPatrol(cur) && createdAt(r) > createdAt(cur))
      || (a === b && isPatrol(r) === isPatrol(cur) && createdAt(r) === createdAt(cur)
          && String(r.reading_id ?? '') > String(cur.reading_id ?? ''))) {
      best.set(d, r)
    }
  }
  return best
}

export function exchangeRowsByDay(rows) {
  const best = latestDeclaringRows(rows)
  for (const [d, r] of [...best]) {
    if (!Array.isArray(r.meter_exchanges) || r.meter_exchanges.length === 0) best.delete(d)
  }
  return best
}

/**
 * その日に「今わかっている報告」(空配列も含む)。報告について何も言っていない日は undefined。
 * ★巡回画面が今日の保存済みの報告を読み戻すのに使う(同じ日に何回保存しても報告を作り直さない)。
 */
export function dayExchangeState(rows, patrolDate) {
  if (!patrolDate) return undefined
  const r = latestDeclaringRows(rows).get(String(patrolDate))
  return r ? normalizeExchanges(r.meter_exchanges) ?? undefined : undefined
}

/**
 * その日の報告に対して、この行の値が区切りの「前側(旧)」か「後側(新)」か(唯一の規則。SQL public.fn_meter_row_side)。
 *   直前の値 final* = ev.final。一周で final が無いときは前の日の値(fallback)。
 *   final* が分かる: 値 < final* なら後側(一周は 0 付近に戻った後、交換は新しいメーター)。それ以外は前側。
 *   final* が分からない(交換で旧最終値不明): その日のイベント行とそれより後の行が後側。
 * @returns {'pre'|'post'}
 */
export function rowSide(ev, value, finalFallback, atOrAfterEventRow) {
  const f = numOrNull(ev?.final) ?? (ev?.type === 'rollover' ? numOrNull(finalFallback) : null)
  const v = numOrNull(value)
  if (f != null && v != null) return v < f ? 'post' : 'pre'
  return atOrAfterEventRow ? 'post' : 'pre'
}

/** 区切りの前側の値が分かるか(stale を判定できるか)。 */
function knownFinal(ev, finalFallback) {
  return numOrNull(ev?.final) ?? (ev?.type === 'rollover' ? numOrNull(finalFallback) : null)
}

/**
 * 行ごとに歩く集計で、1 メーターぶんの差分を古い順に出す(唯一の歩き方。SQL fn_prize_theoretical_stock の pay_rows と同じ)。
 *   - その日の報告は「その日の最初の後側の行」へ入る 1 歩にだけ当てる。
 *   - その日すでに後側へ渡った後に出てくる前側の値の行(交換を知らない古い値の保存 = stale)は増分 0、前回値も据え置く。
 *     ★本番 FKS01-M40-B01 10/5: patrol 31(報告つき)の後に replace 9999987(報告なし)。旧: 31 → 9999987 が +9,999,956、翌日 −10M。
 *   - 値が無い行は null(前回値も無くなる = 旧 D-256 の「片側 null は null」と同じ)。
 * @param {Array<object>} ascRows 古い → 新しい の順(同じ日の中は保存順)
 * @param {string} meter
 * @returns {Array<{delta: number|null, ev: object|null, stale: boolean}>} ascRows と同じ並び
 */
export function walkMeter(ascRows, meter) {
  const rows = Array.isArray(ascRows) ? ascRows : []
  const byDay = exchangeRowsByDay(rows)
  const pos = new Map(rows.map((r, i) => [r, i]))
  const out = []
  let last = null                 // { v, date } 前回値(stale は据え置き)
  let dayBase = null              // 前の日の最後の値(一周の final 省略時)
  let curDate = null
  let crossed = false
  for (const r of rows) {
    const date = String(r?.patrol_date ?? '')
    if (date !== curDate) { curDate = date; crossed = false; dayBase = last?.v ?? null }
    const v = numOrNull(r?.[meter])
    if (v == null) { out.push({ delta: null, ev: null, stale: false }); last = null; continue }
    const evRow = byDay.get(date)
    const ev = evRow ? pickExchange(evRow.meter_exchanges, meter) : null
    if (ev) {
      const at = (pos.get(r) ?? -1) >= (pos.has(evRow) ? pos.get(evRow) : -1)
      const side = rowSide(ev, v, dayBase, at)
      if (side === 'pre' && crossed && knownFinal(ev, dayBase) != null) {
        out.push({ delta: last ? 0 : null, ev: null, stale: true })
        continue
      }
      if (side === 'post' && !crossed) {
        crossed = true
        out.push({ delta: last ? meterBoundaryDelta(last.v, v, ev) : null, ev, stale: false })
        last = { v, date }
        continue
      }
    }
    out.push({ delta: last ? v - last.v : null, ev: null, stale: false })
    last = { v, date }
  }
  return out
}

/**
 * 今日の報告を読み戻したまま値を直したとき、一周の new(参考値 = 区切り直後の値)を今の値に合わせる。
 * ★交換の new は「新しいメーターの取付時の値」(人が入れる値)なので触らない。
 */
export function syncRolloverNewValues(list, curByKey) {
  if (!Array.isArray(list)) return list
  return list.map(e => {
    if (e?.type !== 'rollover') return e
    const n = numOrNull(curByKey?.[e.meter])
    return n != null && e.new !== n ? { ...e, new: n } : e
  })
}

/**
 * 保存前の検知(AC3): 前回より小さい値が入っていて、そのメーターの報告が無いものを返す。
 * @param {{prev: object|null, current: Record<string, number|string|null>, exchanges: Array|null, keys?: string[]}} a
 * @returns {Array<{meter: string, prev: number, cur: number, max: number|null}>}
 */
export function detectUnreportedDrops({ prev, current, exchanges, keys = METER_KEYS } = {}) {
  const out = []
  for (const k of keys) {
    const p = numOrNull(prev?.[k])
    const c = numOrNull(current?.[k])
    if (p == null || c == null || !(c < p)) continue
    if (pickExchange(exchanges, k)) continue
    out.push({ meter: k, prev: p, cur: c, max: inferRolloverMax(p) })
  }
  return out
}

/**
 * シート 1 行ぶんの入力からイベントを作る。最終値の既定は前回値(AC1(c))。
 * @param {{meter: string, type: 'reset'|'rollover', final?: any, newValue?: any, prevValue?: any}} a
 */
export function buildExchange({ meter, type, final, newValue, prevValue, installValue } = {}) {
  // gate_3 3 回目 (5): 交換で直前の値の欄を空にした('')ときは「旧最終値が分からない」= null(前回値で埋めない)。
  const f = type === 'reset' && final === '' ? null : (numOrNull(final) ?? numOrNull(prevValue))
  return normalizeExchange({
    meter, type, final: f,
    // 交換: 新しいメーターの取付時の値(既定 0)/ 一周: 区切り直後の値(参考)。
    new: type === 'reset' ? (numOrNull(installValue) ?? 0) : numOrNull(newValue),
    max: type === 'rollover' ? inferRolloverMax(f) : null,
  })
}

/**
 * シートに出す「この報告で増える分」の説明用。meterBoundaryDelta をそのまま呼ぶ(式を増やさない)。
 */
export function previewExchange(ev, prevValue, curValue) {
  return meterBoundaryDelta(prevValue, curValue, ev)
}

/**
 * 1 件の入力チェック(シートの保存ボタン用)。★止める理由だけを返す(null なら OK)。
 */
export function validateExchangeInput(ev, prevValue, curValue) {
  if (!ev) return '種類とメーターを選んでください'
  const f = numOrNull(ev.final)
  const p = numOrNull(prevValue)
  const c = numOrNull(curValue)
  // 交換は旧最終値を空にできる(分からない = その日の増分は要確認)。一周は直前の値が要る。
  if (f == null && ev.type === 'rollover') return '一周の直前の値を入れてください'
  if (c == null) return '今回のメーター値を先に入れてください'
  if (f != null && p != null && f < p) return `直前の値が前回(${p})より小さいです`
  if (ev.type === 'rollover' && f != null && c >= f) return '一周なら今回の値は直前の値より小さくなります'
  // D-1073: 下がったと報告(取付値 < 旧最終値)しているのにメーターが減っていない(本番 MNK01-M02-B02 10/16 の誤報告)。式でも null になるので確定させない。
  //   取付値 >= 旧最終値(中古メーター型、本番 HMN01-M04 5/12)は止めない(式も計算する)。
  if (ev.type === 'reset' && f != null && resetNotDropped(f, numOrNull(ev.new) ?? 0, c)) return '交換なのにメーターが減っていません。交換していなければ報告を外してください'
  if (ev.type === 'reset' && numOrNull(ev.new) != null && numOrNull(ev.new) > c) return '新しいメーターの取付時の値が今回の値より大きいです'
  return null
}

// ─── AC2: メモに交換/一周の言葉があってもメーター計算には使わない。案内だけ出す ───
const MEMO_HINT_WORDS = ['交換', '一周', '1周', '１周', 'カンスト', 'リセット', '周回']

/** メモに交換/一周らしい言葉があるか(案内表示用。計算には一切使わない)。 */
export function memoMentionsMeterEvent(text) {
  if (typeof text !== 'string' || text.trim() === '') return false
  return MEMO_HINT_WORDS.some(w => text.includes(w))
}

/**
 * 巡回の保存直後に店画面の累計へ足す増分(D-994 gate_3 差し戻し 1 回目 item3)。
 *   prev が前の日以前の行: 共通の式で prev → 今回。
 *   prev が今日の自分の行(再保存): 前の日以前の値(prevDay)から測った「今回の合計 − 先の保存の合計」。
 *     ★報告は先の保存で 1 回効いている。prev(今日の行)に対して一周を当て直すと 1 千万を足し直す(B04 型)。
 *     前の日以前の値が分からないときは、報告が変わっていなければ素の差、変わっていれば足さない(null)。
 */
export function accumIncrementForSave({ prevIsToday, prev, prevDay, numIn, evNow, evLoaded }) {
  const cur = numOrNull(numIn)
  const p = numOrNull(prev?.in_meter)
  if (cur == null || p == null) return null
  if (!prevIsToday) return meterBoundaryDelta(p, cur, evNow ?? null)
  const base = numOrNull(prevDay?.in_meter)
  if (base != null) {
    const now = meterBoundaryDelta(base, cur, evNow ?? null)
    const before = meterBoundaryDelta(base, p, evLoaded ?? null)
    return now == null || before == null ? null : now - before
  }
  const same = JSON.stringify(normalizeExchange(evNow ?? null)) === JSON.stringify(normalizeExchange(evLoaded ?? null))
  return same ? cur - p : null
}
